
import React from 'react';
import { IntegrationFlow, AgentProfile, IntegrationApp, ServiceDefinition, AccessToken, SubscriptionRecord, UserStatistics } from '../../types';
import ProfileOverview from './ProfileOverview';

interface ProfileContentProps {
  activeTab: 'overview' | 'services' | 'flows' | 'agents' | 'integrations' | 'keys' | 'subscriptions';
  servicesData: ServiceDefinition[];
  flows: IntegrationFlow[];
  agents: AgentProfile[];
  integrationApps: IntegrationApp[];
  activeServices: Set<string>;
  totalIntegrationRequests: number;
  totalIntegrationTimeSaved: number;
  tokens: AccessToken[];
  subscriptions: SubscriptionRecord[];
  
  onToggleService: (id: string) => void;
  onToggleFlow: (id: string) => void;
  onToggleAgent: (id: string) => void;
  onOpenCreateKey: () => void;
  onRevokeToken: (id: string) => void;
  onSubscriptionAction: (id: string, action: 'cancel' | 'renew' | 'toggle_renew' | 'pause' | 'resume') => void;
  statistics?: UserStatistics;
}

const ProfileContent: React.FC<ProfileContentProps> = (props) => {
  const {
    activeTab, servicesData, flows, agents, integrationApps, activeServices,
    totalIntegrationRequests, totalIntegrationTimeSaved, tokens, subscriptions,
    onToggleService, onToggleFlow, onToggleAgent, onOpenCreateKey, onRevokeToken, onSubscriptionAction,
    statistics
  } = props;

  const renderStatus = (isActive: boolean, activeText: string, inactiveText: string, color: string = 'emerald') => (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${isActive ? `bg-${color}-500/5 border-${color}-500/20 text-${color}-500` : 'bg-slate-100 dark:bg-white/5 border-transparent text-slate-400'}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${isActive ? `bg-${color}-500 animate-pulse` : 'bg-slate-400'}`}></span>
        <span className="text-[10px] font-black uppercase tracking-wider">{isActive ? activeText : inactiveText}</span>
    </div>
  );

  const renderUsageBar = (percent: number) => (
    <div className="flex items-center gap-3 w-32">
        <div className="flex-1 h-1.5 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${percent}%` }}></div>
        </div>
        <span className="text-[9px] font-bold text-slate-400 w-6 text-right">{percent}%</span>
    </div>
  );

  const renderEmptyState = (icon: string, text: string) => (
      <tr>
          <td colSpan={5} className="py-12 text-center text-slate-400">
              <div className="flex flex-col items-center gap-3 opacity-50">
                  <i className={`fas ${icon} text-3xl`}></i>
                  <span className="text-xs font-bold">{text}</span>
              </div>
          </td>
      </tr>
  );

  if (activeTab === 'overview') {
      return (
          <ProfileOverview 
            language="ar" 
            totalRequests={statistics?.totalRequests || totalIntegrationRequests} 
            timeSaved={statistics?.totalTimeSaved || totalIntegrationTimeSaved} 
            activeServicesCount={statistics?.activeServices || activeServices.size}
            statistics={statistics}
          />
      );
  }

  return (
    <div className="bg-white dark:bg-[#15151a] rounded-[2rem] border border-slate-200 dark:border-white/5 shadow-sm overflow-hidden min-h-[400px] animate-in fade-in slide-in-from-bottom-4">
       {/* ... Key & Subscription Headers remain same ... */}
       {activeTab === 'keys' && (
          <div className="p-6 border-b border-slate-100 dark:border-white/5 flex justify-between items-center bg-slate-50/50 dark:bg-white/[0.01]">
             <div>
                <h3 className="text-sm font-black text-slate-800 dark:text-white uppercase tracking-wider">Access Tokens & Sessions</h3>
                <p className="text-[10px] text-slate-500">Manage your API keys and session validity.</p>
             </div>
             <button onClick={onOpenCreateKey} className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-black shadow-lg shadow-emerald-500/20 active:scale-95 transition-all flex items-center gap-2">
                <i className="fas fa-plus"></i> Create New Key
             </button>
          </div>
       )}

       {activeTab === 'subscriptions' && (
          <div className="p-6 border-b border-slate-100 dark:border-white/5 flex justify-between items-center bg-slate-50/50 dark:bg-white/[0.01]">
             <div>
                <h3 className="text-sm font-black text-slate-800 dark:text-white uppercase tracking-wider">Billing History</h3>
                <p className="text-[10px] text-slate-500">View and manage your subscription plans.</p>
             </div>
          </div>
       )}
       
       <div className="overflow-x-auto">
          <table className="w-full text-left">
             <thead className="bg-slate-50 dark:bg-white/[0.02] text-[10px] uppercase tracking-wider text-slate-400 font-black border-b border-slate-100 dark:border-white/5">
                <tr>
                   <th className="px-6 py-5">Name & Description</th>
                   <th className="px-6 py-5">Category / Type</th>
                   <th className="px-6 py-5">Details</th>
                   <th className="px-6 py-5">Status</th>
                   <th className="px-6 py-5 text-right">Action</th>
                </tr>
             </thead>
             <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {activeTab === 'services' && (servicesData.length > 0 ? servicesData.map(s => (
                   <tr key={s.id} className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors group">
                      <td className="px-6 py-4">
                          <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center text-white shadow-md group-hover:scale-110 transition-transform`}>
                                  <i className={`fas ${s.icon}`}></i>
                              </div>
                              <div>
                                  <h4 className="text-sm font-bold text-slate-800 dark:text-white">{s.title}</h4>
                                  <p className="text-[10px] text-slate-400 max-w-[150px] truncate">{s.description}</p>
                              </div>
                          </div>
                      </td>
                      <td className="px-6 py-4">
                          <span className="px-2 py-1 rounded-md bg-slate-100 dark:bg-white/5 text-[10px] font-bold text-slate-500 uppercase">{s.category}</span>
                      </td>
                      <td className="px-6 py-4">{renderUsageBar(Math.floor(Math.random() * 90) + 10)}</td>
                      <td className="px-6 py-4">{renderStatus(activeServices.has(s.id), 'Active', 'Inactive')}</td>
                      <td className="px-6 py-4 text-right">
                          <button onClick={() => onToggleService(s.id)} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${activeServices.has(s.id) ? 'bg-emerald-500/10 text-emerald-500' : 'bg-slate-100 dark:bg-white/5 text-slate-400'}`}>
                              <i className="fas fa-power-off text-xs"></i>
                          </button>
                      </td>
                   </tr>
                )) : renderEmptyState('fa-cubes', 'No services used yet'))}

                {activeTab === 'flows' && (flows.length > 0 ? flows.map(f => (
                   <tr key={f.id} className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors group">
                      <td className="px-6 py-4">
                          <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md bg-blue-500`}><i className="fas fa-diagram-project"></i></div>
                              <div>
                                  <h4 className="text-sm font-bold text-slate-800 dark:text-white">{f.name}</h4>
                                  <p className="text-[10px] text-slate-400 font-mono">Nodes: {f.nodes.length}</p>
                              </div>
                          </div>
                      </td>
                      <td className="px-6 py-4"><span className="text-xs font-mono text-slate-500">{f.id.substring(0,8)}</span></td>
                      <td className="px-6 py-4">{renderUsageBar(Math.floor(Math.random() * 50))}</td>
                      <td className="px-6 py-4">{renderStatus(f.active, 'Running', 'Paused')}</td>
                      <td className="px-6 py-4 text-right">
                          <button onClick={() => onToggleFlow(f.id)} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${f.active ? 'bg-amber-500/10 text-amber-500' : 'bg-emerald-500/10 text-emerald-500'}`}>
                              <i className={`fas ${f.active ? 'fa-pause' : 'fa-play'} text-xs`}></i>
                          </button>
                      </td>
                   </tr>
                )) : renderEmptyState('fa-diagram-project', 'No automation flows created'))}

                {activeTab === 'agents' && (agents.length > 0 ? agents.map(a => (
                   <tr key={a.id} className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors">
                      <td className="px-6 py-4">
                          <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-xl bg-slate-200 dark:bg-white/10 flex items-center justify-center text-slate-500 dark:text-slate-300"><i className={`fas ${a.avatar}`}></i></div>
                              <div>
                                  <h4 className="text-sm font-bold text-slate-800 dark:text-white">{a.name}</h4>
                                  <p className="text-[10px] text-slate-400">{a.role}</p>
                              </div>
                          </div>
                      </td>
                      <td className="px-6 py-4"><span className="text-[10px] font-bold text-slate-500">{a.dialect}</span></td>
                      <td className="px-6 py-4">{renderUsageBar(Math.floor(Math.random() * 30))}</td>
                      <td className="px-6 py-4">{renderStatus(a.autoReply, 'Enabled', 'Disabled')}</td>
                      <td className="px-6 py-4 text-right">
                          <button onClick={() => onToggleAgent(a.id)} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${a.autoReply ? 'bg-emerald-500/10 text-emerald-500' : 'bg-slate-100 dark:bg-white/5 text-slate-400'}`}>
                              <i className="fas fa-robot text-xs"></i>
                          </button>
                      </td>
                   </tr>
                )) : renderEmptyState('fa-robot', 'No agents created'))}

                {activeTab === 'integrations' && (integrationApps.length > 0 ? integrationApps.map(a => (
                   <tr key={a.id} className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors">
                      <td className="px-6 py-4">
                          <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${a.color} flex items-center justify-center text-white`}><i className={`fa-brands ${a.icon.replace('fa-','') || 'fa-google'} ${a.icon}`}></i></div>
                              <h4 className="text-sm font-bold text-slate-800 dark:text-white">{a.name}</h4>
                          </div>
                      </td>
                      <td className="px-6 py-4"><span className="text-[10px] text-slate-500">External App</span></td>
                      <td className="px-6 py-4">
                          <div className="text-[10px] font-bold text-slate-500">{a.stats.requests} requests</div>
                      </td>
                      <td className="px-6 py-4">{renderStatus(a.connected, 'Connected', 'Disconnected')}</td>
                      <td className="px-6 py-4 text-right">
                          <i className="fas fa-ellipsis-v text-slate-400 text-xs"></i>
                      </td>
                   </tr>
                )) : renderEmptyState('fa-network-wired', 'No active integrations'))}

                {activeTab === 'keys' && (tokens.length > 0 ? tokens.map(t => (
                   <tr key={t.id} className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors">
                      <td className="px-6 py-4">
                          <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-500"><i className="fas fa-key"></i></div>
                              <div>
                                  <h4 className="text-sm font-bold text-slate-800 dark:text-white">{t.name}</h4>
                                  <p className="text-[10px] font-mono text-slate-400">{t.token.substring(0, 12)}...{t.token.substring(t.token.length-4)}</p>
                              </div>
                          </div>
                      </td>
                      <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase ${t.type === 'admin' ? 'bg-rose-500/10 text-rose-500' : t.type === 'read_only' ? 'bg-blue-500/10 text-blue-500' : 'bg-slate-100 dark:bg-white/5 text-slate-500'}`}>
                             {t.type.replace('_', ' ')}
                          </span>
                      </td>
                      <td className="px-6 py-4">
                          <div className="flex flex-col">
                             <span className="text-[10px] font-bold text-slate-500">Created: {new Date(t.createdAt).toLocaleDateString()}</span>
                             <span className="text-[10px] font-bold text-slate-400">Expires: {t.expiresAt ? new Date(t.expiresAt).toLocaleDateString() : 'Never'}</span>
                          </div>
                      </td>
                      <td className="px-6 py-4">{renderStatus(t.status === 'active', 'Active', 'Revoked')}</td>
                      <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                             <button onClick={() => navigator.clipboard.writeText(t.token)} className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-white/5 text-slate-400 hover:text-emerald-500 flex items-center justify-center transition-colors" title="Copy Key">
                                <i className="fas fa-copy text-xs"></i>
                             </button>
                             {t.status === 'active' && (
                               <button onClick={() => onRevokeToken(t.id)} className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-white/5 text-slate-400 hover:bg-rose-500 hover:text-white flex items-center justify-center transition-colors" title="Revoke Key">
                                  <i className="fas fa-trash text-xs"></i>
                               </button>
                             )}
                          </div>
                      </td>
                   </tr>
                )) : renderEmptyState('fa-key', 'No access keys created'))}

                {activeTab === 'subscriptions' && (subscriptions.length > 0 ? subscriptions.map(sub => (
                   <tr key={sub.id} className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors">
                      <td className="px-6 py-4">
                          <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white ${sub.planId === 'pro' ? 'bg-emerald-500' : sub.planId === 'enterprise' ? 'bg-blue-500' : 'bg-slate-500'}`}>
                                 <i className={`fas ${sub.planId === 'pro' ? 'fa-rocket' : sub.planId === 'enterprise' ? 'fa-building-columns' : 'fa-seedling'}`}></i>
                              </div>
                              <div>
                                  <h4 className="text-sm font-bold text-slate-800 dark:text-white uppercase">{sub.planId} PLAN</h4>
                                  <p className="text-[10px] font-mono text-slate-400">{sub.price}</p>
                              </div>
                          </div>
                      </td>
                      <td className="px-6 py-4">
                          <span className="text-[10px] font-bold text-slate-500">{sub.autoRenew ? 'Auto-Renewal On' : 'One-time Payment'}</span>
                      </td>
                      <td className="px-6 py-4">
                          <div className="flex flex-col">
                             <span className="text-[10px] font-bold text-slate-500">Start: {new Date(sub.startDate).toLocaleDateString()}</span>
                             <span className="text-[10px] font-bold text-slate-400">End: {sub.endDate ? new Date(sub.endDate).toLocaleDateString() : 'Forever'}</span>
                          </div>
                      </td>
                      <td className="px-6 py-4">
                         {sub.status === 'active' && renderStatus(true, 'Active', '', 'emerald')}
                         {sub.status === 'paused' && renderStatus(true, 'Paused', '', 'amber')}
                         {sub.status === 'canceled' && renderStatus(false, '', 'Canceled', 'rose')}
                         {sub.status === 'expired' && renderStatus(false, '', 'Expired', 'slate')}
                      </td>
                      <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                             {sub.status === 'active' && (
                                <>
                                   <button onClick={() => onSubscriptionAction(sub.id, 'pause')} className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-white/5 text-slate-400 hover:text-amber-500 flex items-center justify-center transition-colors" title="Pause">
                                      <i className="fas fa-pause text-xs"></i>
                                   </button>
                                   <button onClick={() => onSubscriptionAction(sub.id, 'cancel')} className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-white/5 text-slate-400 hover:bg-rose-500 hover:text-white flex items-center justify-center transition-colors" title="Cancel">
                                      <i className="fas fa-ban text-xs"></i>
                                   </button>
                                </>
                             )}
                             {sub.status === 'paused' && (
                                <button onClick={() => onSubscriptionAction(sub.id, 'resume')} className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center transition-colors" title="Resume">
                                   <i className="fas fa-play text-xs"></i>
                                </button>
                             )}
                             {(sub.status === 'expired' || sub.status === 'canceled') && (
                                <button onClick={() => onSubscriptionAction(sub.id, 'renew')} className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-white/5 text-emerald-500 hover:bg-emerald-500 hover:text-white flex items-center justify-center transition-colors" title="Renew">
                                   <i className="fas fa-rotate-right text-xs"></i>
                                </button>
                             )}
                          </div>
                      </td>
                   </tr>
                )) : renderEmptyState('fa-file-invoice-dollar', 'No subscription history'))}
             </tbody>
          </table>
       </div>
    </div>
  );
};
export default ProfileContent;
